"use client"

import { Show, SignInButton, useClerk, useUser } from "@clerk/react"
import { ChevronsUpDown, LogOut, Settings } from "lucide-react"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	useSidebar,
} from "@/components/ui/sidebar"

function UserAvatar() {
	const { user } = useUser()

	return (
		<Avatar className="h-8 w-8 rounded-lg">
			<AvatarImage src={user?.imageUrl} alt={user?.fullName || ""} />
			<AvatarFallback className="rounded-lg">
				{user?.firstName?.[0]}
				{user?.lastName?.[0]}
			</AvatarFallback>
		</Avatar>
	)
}

function UserNameAndEmail() {
	const { user } = useUser()

	return (
		<div className="grid flex-1 text-left text-sm leading-tight">
			<span className="truncate font-medium">
				{user?.fullName || user?.username || "User"}
			</span>
			<span className="truncate text-xs">
				{user?.primaryEmailAddress?.emailAddress}
			</span>
		</div>
	)
}

function UserMenuContent({
	side,
	className,
}: {
	side: "bottom" | "right"
	className?: string
}) {
	const { signOut, openUserProfile } = useClerk()

	return (
		<DropdownMenuContent
			className={className ?? "min-w-56 rounded-lg"}
			side={side}
			align="end"
			sideOffset={4}
		>
			<DropdownMenuLabel className="p-0 font-normal">
				<div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
					<UserAvatar />
					<UserNameAndEmail />
				</div>
			</DropdownMenuLabel>
			<DropdownMenuSeparator />
			<DropdownMenuGroup>
				<DropdownMenuItem onClick={() => openUserProfile()}>
					<Settings />
					Manage account
				</DropdownMenuItem>
			</DropdownMenuGroup>
			<DropdownMenuSeparator />
			<DropdownMenuItem onClick={() => signOut({ redirectUrl: "/" })}>
				<LogOut />
				Sign out
			</DropdownMenuItem>
		</DropdownMenuContent>
	)
}

/** The signed-in admin's avatar alone, at the bottom of the navigation rail. */
export function RailUserProfileToggle() {
	const { user, isLoaded } = useUser()

	if (!isLoaded) {
		return (
			<Avatar className="h-8 w-8 rounded-lg">
				<AvatarFallback className="rounded-lg">--</AvatarFallback>
			</Avatar>
		)
	}

	return (
		<>
			<Show when="signed-in">
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<button
							type="button"
							className="cursor-pointer rounded-lg outline-hidden focus-visible:ring-2 focus-visible:ring-white/50"
							aria-label="Account menu"
							title={user?.fullName || user?.username || "Account"}
						>
							<UserAvatar />
						</button>
					</DropdownMenuTrigger>
					<UserMenuContent side="right" />
				</DropdownMenu>
			</Show>
			<Show when="signed-out">
				<SignInButton mode="modal">
					<button
						type="button"
						className="cursor-pointer rounded-lg"
						aria-label="Sign in"
					>
						<Avatar className="h-8 w-8 rounded-lg">
							<AvatarFallback className="rounded-lg">--</AvatarFallback>
						</Avatar>
					</button>
				</SignInButton>
			</Show>
		</>
	)
}

export default function UserProfileToggle() {
	const { isLoaded } = useUser()
	const { isMobile } = useSidebar()

	if (!isLoaded) {
		return (
			<SidebarMenu>
				<SidebarMenuItem>
					<SidebarMenuButton size="lg" disabled>
						<Avatar className="h-8 w-8 rounded-lg">
							<AvatarFallback className="rounded-lg">--</AvatarFallback>
						</Avatar>
						<div className="grid flex-1 text-left text-sm leading-tight">
							<span className="truncate font-medium">Loading...</span>
						</div>
					</SidebarMenuButton>
				</SidebarMenuItem>
			</SidebarMenu>
		)
	}

	return (
		<SidebarMenu>
			<SidebarMenuItem>
				<Show when="signed-in">
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<SidebarMenuButton
								size="lg"
								className="w-full cursor-pointer data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
							>
								<UserAvatar />
								<UserNameAndEmail />
								<ChevronsUpDown className="ml-auto size-4" />
							</SidebarMenuButton>
						</DropdownMenuTrigger>
						<UserMenuContent
							side={isMobile ? "bottom" : "right"}
							className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
						/>
					</DropdownMenu>
				</Show>
				<Show when="signed-out">
					<SignInButton mode="modal">
						<SidebarMenuButton size="lg" className="w-full">
							<Avatar className="h-8 w-8 rounded-lg">
								<AvatarFallback className="rounded-lg">--</AvatarFallback>
							</Avatar>
							<div className="grid flex-1 text-left text-sm leading-tight">
								<span className="truncate font-medium">Sign In</span>
								<span className="truncate text-xs">Login to continue</span>
							</div>
						</SidebarMenuButton>
					</SignInButton>
				</Show>
			</SidebarMenuItem>
		</SidebarMenu>
	)
}
